import { FlexCol, FlexRow } from '@components/layout/Flex';
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Shadow } from './ShadowScroll.style';

type ShadowScrollProps = {
    direction?: 'vertical' | 'horizontal';
    children?: ReactNode;
    gap?: number;
    grow?: number;
};

export type ShadowScrollRef = HTMLDivElement & {
    scrollToEnd(): void;
};

export const ShadowScroll = forwardRef<ShadowScrollRef, ShadowScrollProps>((props, ref) => {
    const { direction = 'vertical', children, gap = 4, grow = 0 } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    const shadowStartRef = useRef<HTMLDivElement>(null);
    const shadowEndRef = useRef<HTMLDivElement>(null);

    const isVertical = direction === 'vertical';
    const Container = isVertical ? FlexCol : FlexRow;
    const startPosition = isVertical ? 'top' : 'left';
    const endPosition = isVertical ? 'bottom' : 'right';
    const minSize = isVertical ? 'minHeight' : 'minWidth';
    const scrollStart = isVertical ? 'scrollTop' : 'scrollLeft';
    const scrollSize = isVertical ? 'scrollHeight' : 'scrollWidth';
    const offsetSize = isVertical ? 'offsetHeight' : 'offsetWidth';

    const scrollToEnd = useCallback(() => {
        // Scroll after any actions or events when dom has been painted
        requestAnimationFrame(() =>
            scrollRef.current?.scrollTo({ [startPosition]: scrollRef.current[scrollSize], behavior: 'smooth' })
        );
    }, [startPosition, scrollSize]);

    useImperativeHandle(ref, () => ({ ...scrollRef.current!, scrollToEnd }), [scrollToEnd]);

    const handleScrollShade = useCallback(() => {
        if (!(scrollRef.current && shadowStartRef.current && shadowEndRef.current)) return;

        shadowStartRef.current.style.display = scrollRef.current[scrollStart] === 0 ? 'none' : 'block';
        shadowEndRef.current.style.display =
            Math.round(scrollRef.current[scrollStart]) >= scrollRef.current[scrollSize] - scrollRef.current[offsetSize]
                ? 'none'
                : 'block';
    }, [scrollStart, scrollSize, offsetSize]);

    useLayoutEffect(handleScrollShade);

    useEffect(() => {
        const { current: scroll } = scrollRef;
        scroll?.addEventListener?.('scroll', handleScrollShade);
        window.addEventListener('resize', handleScrollShade);

        return () => {
            scroll?.removeEventListener?.('scroll', handleScrollShade);
            window.removeEventListener('resize', handleScrollShade);
        };
    }, [handleScrollShade]);

    return (
        <>
            <Container $flexGrow={grow} style={{ position: 'relative', [minSize]: 0 }}>
                <Shadow $position={startPosition} ref={shadowStartRef} />
                <Container ref={scrollRef} $gap={gap} style={{ overflow: 'auto' }}>
                    {children}
                </Container>
                <Shadow $position={endPosition} ref={shadowEndRef} />
            </Container>
        </>
    );
});

ShadowScroll.displayName = 'ShadowScroll';
